<template>
  <div class="page-shell backtest-view">
    <!-- 配置选择 + 刷新 -->
    <div class="toolbar">
      <span class="toolbar-label">回测配置</span>
      <el-select v-model="tag" class="toolbar-select" size="small" @change="load">
        <el-option
          v-for="item in list"
          :key="item.tag"
          :value="item.tag"
          :label="optionLabel(item)"
        />
      </el-select>
      <span v-if="data" class="meta">更新于 {{ generatedAtText }}</span>
      <el-button size="small" icon="el-icon-refresh" :loading="loading" @click="load">刷新</el-button>
    </div>

    <div v-if="error" class="error-bar">
      <el-alert :title="error" type="warning" :closable="false" show-icon />
    </div>

    <template v-if="data">
      <!-- 统计卡片 -->
      <div class="stat-cards">
        <div class="stat-card">
          <div class="stat-label">最新信号日</div>
          <div class="stat-value">{{ latest ? latest.signalDate : '—' }}</div>
          <div class="stat-sub">
            池内 {{ latest ? latest.n : '—' }} 只 · 开仓 {{ combined.activePeriods }}/{{ combined.periods }} 期
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-label">IC 均值 / ICIR</div>
          <div class="stat-value">{{ pct(combined.ic.mean, 3) }} / {{ num(combined.ic.icir, 2) }}</div>
          <div class="stat-sub">正率 {{ pct(combined.ic.posRate, 1) }} · 样本 {{ combined.ic.n }} 期</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">累计多头（扣{{ costText }}）</div>
          <div class="stat-value" :class="upDownClass(combined.cum.long)">{{ pct(combined.cum.long, 1) }}</div>
          <div class="stat-sub">池等权 {{ pct(combined.cum.bench, 1) }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">累计超额</div>
          <div class="stat-value" :class="upDownClass(combined.cum.excess)">{{ pct(combined.cum.excess, 1) }}</div>
          <div class="stat-sub">超额正率 {{ pct(combined.excessPosRate, 1) }}</div>
        </div>
      </div>

      <!-- 口径说明条：策略口径 + 池快照区间。18 段明细收进 hover 气泡，不再撑高 KPI 卡片行 -->
      <div class="caliber-bar">
        <div class="caliber-main">
          <span class="caliber-label">策略口径</span>
          <span class="caliber-value">{{ strategyText }}</span>
        </div>
        <div class="caliber-seg">
          <span class="caliber-label">池快照区间</span>
          <span class="caliber-value-sm">{{ segmentSummary }}</span>
          <el-popover
            placement="bottom-end"
            width="300"
            trigger="hover"
            popper-class="seg-popover"
            :open-delay="60"
          >
            <div class="seg-pop">
              <div class="seg-pop-head">{{ segments.length }} 段区间（按季报披露截止日切换）</div>
              <div v-for="seg in segments" :key="seg.quarter" class="seg-pop-line">
                <span class="seg-q">{{ seg.quarter }}</span>
                <span>{{ seg.window }}</span>
              </div>
            </div>
            <span slot="reference" class="seg-more">区间明细</span>
          </el-popover>
        </div>
      </div>

      <!-- 图表：IC 序列 + 累计净值 -->
      <div class="chart-row">
        <div class="chart-card">
          <div class="chart-title">周度 IC 序列</div>
          <div ref="icChart" class="chart" />
        </div>
        <div class="chart-card">
          <div class="chart-title">累计净值：多头 Top-N vs 池等权</div>
          <div ref="navChart" class="chart" />
        </div>
      </div>

      <!-- 最新一期持仓 -->
      <div v-if="latestPicks.length" class="block">
        <div class="block-title">
          最新一期 Top-N 持仓（信号日 {{ latest.signalDate }}，按因子值降序）
          <span class="caliber-hint">前 10 名 = 实盘口径（单票 ≤10%）；全部 = 研究口径（等权 ≈5.6%，仅研究用）</span>
        </div>
        <el-table :data="latestPicks" size="mini" border stripe max-height="320" :row-class-name="picksRowClass">
          <el-table-column label="#" width="50">
            <template slot-scope="{ $index }">{{ $index + 1 }}</template>
          </el-table-column>
          <el-table-column label="口径" width="86">
            <template slot-scope="{ $index }">
              <el-tag v-if="$index < liveTop" type="danger" size="mini">实盘</el-tag>
              <el-tag v-else type="info" size="mini">研究</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="code" label="代码" width="90">
            <template slot-scope="{ row }">
              <el-link
                :href="stockstarCodeLink(row.code)"
                target="_blank"
                rel="noopener noreferrer"
              >{{ row.code }}</el-link>
            </template>
          </el-table-column>
          <el-table-column prop="name" label="名称" min-width="120">
            <template slot-scope="{ row }">
              <el-link
                :href="eastmoneyCodeLink(row)"
                target="_blank"
                rel="noopener noreferrer"
              >{{ row.name }}</el-link>
            </template>
          </el-table-column>
          <el-table-column label="因子值" width="110" align="right">
            <template slot-scope="{ row }">
              {{ formatFactorValue(config.factor, row.raw !== undefined ? row.raw : row.factor) }}
              <div v-if="row.raw !== undefined && config.neutral === 'industry'" class="factor-pct">行业内 P{{ Math.round(row.factor * 100) }}</div>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <!-- 逐期明细 -->
      <div class="block">
        <div class="block-title">逐期明细</div>
        <el-table
          :data="allPeriods"
          size="mini"
          border
          stripe
          max-height="420"
          :default-sort="{ prop: 'signalDate', order: 'descending' }"
        >
          <el-table-column prop="signalDate" label="信号日" width="118" sortable />
          <el-table-column prop="quarter" label="池快照" width="90" />
          <el-table-column prop="n" label="池内N" width="70" align="right" />
          <el-table-column label="IC" width="90" align="right">
            <template slot-scope="{ row }">{{ row.ic === null ? '空仓' : num(row.ic, 3) }}</template>
          </el-table-column>
          <el-table-column label="多头净收益" width="110" align="right">
            <template slot-scope="{ row }">
              <span :class="upDownClass(row.longRet)">{{ pct(row.longRet, 2) }}</span>
            </template>
          </el-table-column>
          <el-table-column label="池等权" width="100" align="right">
            <template slot-scope="{ row }">
              <span :class="upDownClass(row.benchRet)">{{ pct(row.benchRet, 2) }}</span>
            </template>
          </el-table-column>
          <el-table-column label="超额" width="100" align="right">
            <template slot-scope="{ row }">
              <span :class="upDownClass(row.excess)">{{ pct(row.excess, 2) }}</span>
            </template>
          </el-table-column>
          <el-table-column label="累计多头净值" min-width="120" align="right">
            <template slot-scope="{ row }">{{ num(row.cumNav, 4) }}</template>
          </el-table-column>
        </el-table>
        <div class="table-foot">
          <span>默认按信号日倒序（最新在上）；点「信号日」表头可切回正序。</span>
          <span v-if="latest" class="table-foot-hint">最新一期：{{ latest.signalDate }}</span>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import * as echarts from 'echarts/core'
import { BarChart, LineChart } from 'echarts/charts'
import {
  GridComponent,
  LegendComponent,
  TooltipComponent,
} from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'
import { fetchRollingBacktest } from '../utils/backtestApi.js'
import { formatFactorValue } from '../utils/factorFormat.js'
import { eastmoneyCodeLink, stockstarCodeLink } from '../utils/stockLink.js'

echarts.use([
  BarChart,
  LineChart,
  GridComponent,
  LegendComponent,
  TooltipComponent,
  CanvasRenderer,
])

// 因子中文名（r5/r10 取原始区间涨幅 = 动量；rev* 取负 = 反转/超跌）
const FACTOR_LABELS = {
  r5: '近5日动量',
  r10: '近10日动量',
  rev5: '近5日反转',
  rev10: '近10日反转',
  vr: '量能比',
  mom60: '60日动量',
  mom120_20: '120日动量(剔近20日)',
  near_high: '52周高点距离',
  rsrs: 'RSRS趋势强度',
  sharpe60: '60日夏普动量',
  crash5: '近5日最大单日跌幅',
  crash10: '近10日最大单日跌幅',
  np_yoy: '净利同比增速',
  np_accel: '净利增速加速度',
  roe: '净资产收益率ROE',
  ep: '盈利收益率E/P',
  bp: '账面市值比B/P',
  cfq: '现金流质量倍数',
}

export default {
  name: 'BacktestView',
  props: {
    // 由 App.vue 传入当前菜单是否可见（本页用 v-show 常驻，隐藏时容器尺寸为 0）
    visible: { type: Boolean, default: true },
  },
  data() {
    return {
      loading: false,
      error: '',
      tag: '',
      list: [],
      data: null,
      icChart: null,
      navChart: null,
      chartObserver: null, // ResizeObserver：容器尺寸变化（含首屏布局完成、从隐藏恢复）时补渲染
      chartRetry: 0, // 容器尚无有效尺寸时的重试次数（防止 init 出 0 尺寸图表）
      chartRetryTimer: null,
      liveTop: 10, // 实盘口径持仓数（单票 ≤10%）；其余为研究口径
    }
  },
  computed: {
    combined() {
      return this.data?.combined || { ic: {}, cum: {}, periods: 0, activePeriods: 0 }
    },
    latest() {
      return this.combined.latest || null
    },
    config() {
      return this.data?.config || {}
    },
    segments() {
      return this.data?.segments || []
    },
    allPeriods() {
      const out = []
      for (const seg of this.segments) {
        let nav = 1
        for (const p of seg.result.periods || []) {
          nav *= 1 + (p.longRet || 0)
          out.push({ ...p, quarter: seg.quarter, cumNav: nav })
        }
      }
      return out
    },
    latestPicks() {
      const p = this.allPeriods[this.allPeriods.length - 1]
      return p?.picks || []
    },
    strategyText() {
      const c = this.config
      if (!c.pool) return '—'
      const pool = c.pool === 'p75' ? 'P75 严格池' : 'Top500 池'
      const factor = this.factorLabel(c.factor)
      const neutral = c.neutral === 'industry' ? ' · 行业中性' : ''
      return `${pool} · ${factor} · 持有${c.hold}日${neutral}`
    },
    /** 池快照区间压成一行：起 → 止 + 段数（逐段明细见 hover 气泡） */
    segmentSummary() {
      const segs = this.segments
      if (!segs.length) return '—'
      const first = String(segs[0].window || '').split('~')[0].trim()
      const last = String(segs[segs.length - 1].window || '').split('~').pop().trim()
      return `${first} → ${last} · 共 ${segs.length} 段`
    },
    generatedAtText() {
      const raw = this.data?.generatedAt
      if (!raw) return '—'
      return raw.replace('T', ' ').slice(0, 19)
    },
    /**
     * 交易成本口径文案。config.cost 来自归档 = 「一条腿一次完整买卖」（买 + 卖）的双边成本。
     * 不在此硬编码数值：口径调整后归档重跑即自动跟随。
     */
    costText() {
      const c = this.config.cost
      if (typeof c !== 'number') return '双边成本'
      const wan = c * 10000
      return `双边成本 万${Number.isInteger(wan) ? wan : wan.toFixed(1)}`
    },
  },
  watch: {
    // 从别的菜单切回来：v-show 由 display:none 恢复，需强制补一次完整渲染
    // （隐藏期间 ResizeObserver 会以 0 尺寸触发，避免画布被 resize 到 0 后不恢复）
    visible(val) {
      if (val) this.$nextTick(() => this.renderCharts())
    },
  },
  mounted() {
    // 首屏加载必须在 mounted（DOM 已挂载）后再发起，否则图表容器无宽度
    this.load()
    window.addEventListener('resize', this.resizeCharts)
    document.addEventListener('visibilitychange', this.onVisibilityChange)
  },
  beforeDestroy() {
    this.icChart?.dispose()
    this.navChart?.dispose()
    this.chartObserver?.disconnect()
    clearTimeout(this.chartRetryTimer)
    window.removeEventListener('resize', this.resizeCharts)
    document.removeEventListener('visibilitychange', this.onVisibilityChange)
  },
  methods: {
    optionLabel(item) {
      return `${item.tag} · 最新 ${item.latestSignalDate || '—'} · 超额 ${this.pct(item.cumExcess, 1)}`
    },
    /** 因子中文名；合成因子（如 r10+rsrs）逐段翻译后用 + 连接 */
    factorLabel(factor) {
      if (!factor) return '—'
      return String(factor)
        .split('+')
        .map((f) => FACTOR_LABELS[f] || f)
        .join(' + ')
    },
    picksRowClass({ rowIndex }) {
      return rowIndex < this.liveTop ? 'picks-live-row' : ''
    },
    async load() {
      this.loading = true
      this.error = ''
      try {
        const res = await fetchRollingBacktest(this.tag)
        this.list = res.list || []
        this.tag = res.tag
        this.data = res.data
        this.$nextTick(() => {
          this.setupChartObserver()
          this.renderCharts()
        })
      } catch (err) {
        this.error = err.message || '加载回测结果失败'
      } finally {
        this.loading = false
      }
    },
    onVisibilityChange() {
      if (!document.hidden) this.$nextTick(() => this.renderCharts())
    },
    /**
     * 监听两个图表容器：容器刚拿到非零尺寸（首屏布局完成 / 从隐藏恢复 / 侧边栏折叠）
     * 时补一次完整渲染，之后仅做 resize。
     * 必须在 $refs 就绪后调用（图表块受 v-if="data" 控制，数据到达前无 DOM）。
     */
    setupChartObserver() {
      if (typeof ResizeObserver === 'undefined') return
      const els = [this.$refs.icChart, this.$refs.navChart].filter(Boolean)
      if (!els.length) return
      this.chartObserver?.disconnect()
      this.chartObserver = new ResizeObserver(() => {
        if (!this.icChart || !this.navChart) this.renderCharts()
        else this.resizeCharts()
      })
      els.forEach((el) => this.chartObserver.observe(el))
    },
    /** 容器可用尺寸；隐藏（display:none）或尚未布局时为 0 */
    chartBox(el) {
      return { w: el?.clientWidth || 0, h: el?.clientHeight || 0 }
    },
    renderCharts() {
      const periods = this.allPeriods
      const icEl = this.$refs.icChart
      const navEl = this.$refs.navChart
      if (!periods.length || !icEl || !navEl) return

      // 容器尚无有效尺寸（首屏布局未完成、页面隐藏）→ 延时重试；
      // 不设"永久放弃"标记：ResizeObserver / visible 变更都会再触发本方法
      const box = this.chartBox(icEl)
      if (!box.w || !box.h) {
        if (this.visible && this.chartRetry < 40) {
          this.chartRetry += 1
          clearTimeout(this.chartRetryTimer)
          this.chartRetryTimer = setTimeout(() => this.renderCharts(), 100)
        }
        return
      }
      this.chartRetry = 0

      // 图表块可能在 data 刷新/菜单切换时被重建：实例绑定的 DOM 已不是当前容器则重建实例
      if (!this.icChart || this.icChart.getDom() !== icEl) {
        this.icChart?.dispose()
        this.icChart = echarts.init(icEl)
      }
      if (!this.navChart || this.navChart.getDom() !== navEl) {
        this.navChart?.dispose()
        this.navChart = echarts.init(navEl)
      }

      const dates = periods.map((p) => p.signalDate)
      const axisCommon = { type: 'category', data: dates, axisLabel: { rotate: 45, fontSize: 10, hideOverlap: true } }
      // IC 柱状图：正值红 / 负值绿（A股惯例）
      this.icChart.setOption({
        tooltip: { trigger: 'axis' },
        grid: { left: 50, right: 16, top: 24, bottom: 48 },
        xAxis: axisCommon,
        yAxis: { type: 'value' },
        series: [{
          type: 'bar',
          name: 'IC',
          data: periods.map((p) => ({
            value: p.ic === null ? null : Number(p.ic.toFixed(3)),
            itemStyle: { color: p.ic >= 0 ? '#f56c6c' : '#67c238' },
          })),
        }],
      })

      // 累计净值：多头 vs 池等权
      let longNav = 1
      let benchNav = 1
      const longLine = periods.map((p) => {
        longNav *= 1 + (p.longRet || 0)
        return Number(longNav.toFixed(4))
      })
      const benchLine = periods.map((p) => {
        benchNav *= 1 + (p.benchRet || 0)
        return Number(benchNav.toFixed(4))
      })
      this.navChart.setOption({
        tooltip: { trigger: 'axis' },
        legend: { top: 0, data: ['多头 Top-N', '池等权'] },
        grid: { left: 50, right: 16, top: 30, bottom: 48 },
        xAxis: axisCommon,
        yAxis: { type: 'value', scale: true },
        series: [
          { type: 'line', name: '多头 Top-N', data: longLine, showSymbol: false, lineStyle: { width: 2, color: '#f56c6c' }, itemStyle: { color: '#f56c6c' } },
          { type: 'line', name: '池等权', data: benchLine, showSymbol: false, lineStyle: { width: 2, color: '#909399' }, itemStyle: { color: '#909399' } },
        ],
      })

      // 两次 setOption 后按当前容器尺寸对齐一次
      this.resizeCharts()
    },
    resizeCharts() {
      // 页面隐藏（display:none）时容器为 0 尺寸，直接 resize 会把画布压成 0
      // 且不再恢复，因此仅在容器有有效尺寸时校正
      const icBox = this.chartBox(this.$refs.icChart)
      const navBox = this.chartBox(this.$refs.navChart)
      if (this.icChart && icBox.w && icBox.h) this.icChart.resize()
      if (this.navChart && navBox.w && navBox.h) this.navChart.resize()
    },
    pct(v, digits = 2) {
      return v === null || v === undefined ? '—' : `${(v * 100).toFixed(digits)}%`
    },
    num(v, digits = 2) {
      return v === null || v === undefined ? '—' : Number(v).toFixed(digits)
    },
    // 因子值按量纲展示（% / 万% / 比率），仅影响可读性、不改变排序
    formatFactorValue,
    // 代码跳证券之星、名称跳东方财富（与基金重仓/波段雷达等页面保持一致）
    eastmoneyCodeLink,
    stockstarCodeLink,
    upDownClass(v) {
      if (v === null || v === undefined) return ''
      return v > 0 ? 'txt-up' : (v < 0 ? 'txt-down' : '')
    },
  },
}
</script>

<style scoped>
.backtest-view {
  padding: 4px;
}

.toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
}

.toolbar-label {
  font-weight: 600;
  color: #2c3e50;
  flex: 0 0 auto;
  white-space: nowrap;
}

.toolbar-select {
  width: 260px;
}

.meta {
  color: #909399;
  font-size: 12px;
}

.error-bar {
  margin-bottom: 14px;
}

.stat-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: 12px;
  margin-bottom: 14px;
}

.stat-card {
  background: #f8f9fb;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  padding: 12px 14px;
}

.stat-label {
  font-size: 12px;
  color: #909399;
  margin-bottom: 6px;
}

.stat-value {
  font-size: 22px;
  font-weight: 700;
  color: #303133;
}

.stat-sub {
  margin-top: 6px;
  font-size: 12px;
  color: #909399;
}

/* 口径说明条：横向一行，位于 KPI 卡片下方 */
.caliber-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 28px;
  background: #f8f9fb;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  padding: 9px 14px;
  margin-bottom: 14px;
  font-size: 13px;
  line-height: 1.5;
}

.caliber-main,
.caliber-seg {
  display: flex;
  align-items: center;
  min-width: 0;
}

.caliber-label {
  color: #909399;
  margin-right: 8px;
  white-space: nowrap;
}

.caliber-value {
  color: #303133;
  font-weight: 600;
}

.caliber-value-sm {
  color: #606266;
}

.seg-more {
  margin-left: 10px;
  color: #409eff;
  cursor: pointer;
  white-space: nowrap;
  border-bottom: 1px dashed #a0cfff;
}

/* 移动端（断点与 utils/device.js 的 768px 保持一致）：
   工具栏第一行 = 配置 + 下拉 + 刷新，「更新于 xxx」独占第二行小字；
   口径条改为上下两行，避免窄屏挤压 */
@media (max-width: 768px) {
  .toolbar {
    flex-wrap: wrap;
    gap: 8px;
    row-gap: 4px;
    margin-bottom: 10px;
  }

  /* 下拉吃掉第一行剩余宽度 */
  .toolbar-select {
    flex: 1 1 0;
    width: auto;
    min-width: 0;
  }

  /* 更新时间独占第二行：不压缩下拉，也不消失 */
  .meta {
    order: 3;
    flex: 1 1 100%;
    font-size: 11px;
  }

  .stat-cards {
    gap: 8px;
    margin-bottom: 10px;
  }

  .caliber-bar {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    padding: 8px 12px;
    margin-bottom: 10px;
    font-size: 12px;
  }

  .caliber-main,
  .caliber-seg {
    flex-wrap: wrap;
  }

  /* 底部说明：窄屏下两行堆叠，避免长句挤成锯齿 */
  .table-foot {
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
    font-size: 11px;
  }
}

.chart-row {
  display: grid;
  /* minmax(0,1fr)：ECharts 塞入固定像素宽的 canvas 后会把网格项 min-content 撑大，
     用 1fr 会导致列宽反馈式增长（第二张卡被挤出容器），必须把最小尺寸压到 0 */
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 12px;
  margin-bottom: 14px;
}

@media (max-width: 1100px) {
  .chart-row {
    grid-template-columns: minmax(0, 1fr);
  }
}

.chart-card {
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  padding: 10px 12px;
  min-width: 0;
}

.chart-title {
  font-size: 13px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 4px;
}

.chart {
  width: 100%;
  min-width: 0;
  height: 280px;
  overflow: hidden;
}

.block {
  margin-bottom: 18px;
}

.block-title {
  font-size: 13px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 8px;
}

.caliber-hint {
  font-size: 12px;
  font-weight: 400;
  color: #909399;
  margin-left: 10px;
}

/* 表格底部说明：排序提示 + 最新一期锚点 */
.table-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 6px;
  font-size: 12px;
  color: #909399;
}

.table-foot-hint {
  flex: 0 0 auto;
  color: #606266;
}

.factor-pct {
  font-size: 11px;
  color: #909399;
  line-height: 1.2;
}

::v-deep .el-table .picks-live-row {
  background: #fdf6ec;
}

::v-deep .el-table .picks-live-row td {
  border-bottom: 1px solid #f5dab1;
}

.txt-up {
  color: #f56c6c;
  font-weight: 600;
}

.txt-down {
  color: #67c238;
  font-weight: 600;
}
</style>

<style>
/* el-popover 内容默认挂载到 body，scoped 样式不生效，用 popper-class 单独定义 */
.seg-popover .seg-pop-head {
  font-size: 12px;
  color: #909399;
  margin-bottom: 6px;
}

.seg-popover .seg-pop-line {
  display: flex;
  gap: 10px;
  font-size: 12px;
  color: #303133;
  line-height: 1.7;
}

.seg-popover .seg-pop-line .seg-q {
  flex: 0 0 58px;
  color: #606266;
}
</style>
